/**
 * Lets a nested native scroller keep its own horizontal touch gesture when it sits
 * inside an Embla carousel.
 *
 * The product-card image slider is a CSS scroll-snap track, and a product card can sit
 * inside a carousel (home-page product rails, product tabs, bundles). Both want the
 * same swipe, and Embla wins: it claims the first `touchmove` and calls
 * `preventDefault()`, so without this the nested track never scrolls on mobile — the
 * carousel moves instead and the card's extra images are simply unreachable.
 *
 * `watchDrag` is Embla's supported way to decline a gesture (`DragHandler` only calls
 * `down(evt)` when the callback returns truthy). Returning false means Embla never
 * starts a drag for THIS pointerdown, so the browser handles the gesture natively: the
 * nested track pans horizontally, and vertical page scrolling is unaffected either way
 * because Embla was never going to handle that.
 *
 * The resolution: a swipe that STARTS on the product image pages the images; a swipe
 * anywhere else on the card — name, price, the actions row, the gaps between cards —
 * drags the carousel, as do the carousel's own arrows. That is the same handoff
 * Instagram and Amazon use for a multi-image card inside a scrolling feed. It costs
 * nothing on single-image products: those render no track at all, so the attribute is
 * absent and the carousel still drags from anywhere on the card.
 *
 * This module deliberately imports NOTHING. The product slider needs the attribute, and
 * importing it from `Embla-slider/index` would pull `embla-carousel` into the product
 * listing bundle — which is the whole cost the CSS slider exists to avoid.
 */
export const NESTED_SCROLLER_ATTR = "data-owns-h-gesture";

/** Spread onto the scroller that should keep its own horizontal gesture. */
export const nestedScrollerProps = { [NESTED_SCROLLER_ATTR]: "" } as const;

/**
 * Whether `target` sits inside an element that owns its own horizontal gesture.
 *
 * Used both to keep Embla out of the way and to keep the product card's press-scale
 * feedback off a pan: a card that shrinks and fades while the shopper swipes its images
 * is wrong on its own terms, and on iOS a transform/compositing change applied to an
 * ancestor at the exact moment the gesture is being routed is a known way to lose the
 * nested scroll entirely.
 */
export const ownsHorizontalGesture = (target: EventTarget | null): boolean =>
  target instanceof Element && !!target.closest(`[${NESTED_SCROLLER_ATTR}]`);

/**
 * Embla `watchDrag` predicate. Module-level so its identity is stable — `areOptionsEqual`
 * compares option functions by source string, and a per-render closure would be
 * re-serialised on every render of every carousel.
 */
export const watchDragOutsideNestedScroller = (
  _emblaApi: unknown,
  evt: TouchEvent | MouseEvent,
): boolean => !ownsHorizontalGesture(evt.target);
